import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import PhoneIcon from '@mui/icons-material/Phone';
import SendIcon from '@mui/icons-material/Send';
import { DateTime } from 'luxon';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EmailTemplatesPopover, useDialogs } from '@work4all/components';
import { CustomToolbarAction } from '@work4all/components/lib/dataDisplay/actions-bar/ToolbarTypes';
import { useEmailTemplates, useLock } from '@work4all/components/lib/hooks';

import { useDataProvider, useUser } from '@work4all/data';

import { EMailTemplate } from '@work4all/models/lib/Classes/EMailTemplate.entity';
import { Ticket } from '@work4all/models/lib/Classes/Ticket.entity';
import { User } from '@work4all/models/lib/Classes/User.entity';
import { DataRequest } from '@work4all/models/lib/DataProvider';
import { EMailTemplateKind } from '@work4all/models/lib/Enums/EMailTemplateKind.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { sdObjTypeToEntity } from '@work4all/utils';
import { canAddAppointment } from '@work4all/utils/lib/permissions';

import { MaskDialogOverlay } from '../../../../../components/entity-picker/MaskDialogOverlay';
import { useTicketContactsPopoverData } from '../../../../../hooks/useTicketContacts';
import { settings, useSetting } from '../../../../../settings';
import { htmlToPlainText } from '../../../../../utils/htmlToPlainText';
import { AppointmentQuickCreateDialog } from '../../../../calendar/components/AppointmentQuickCreateDialog';
import { mapUsersToAppointmentAttendees } from '../../../../calendar/utils/map-users-to-appointment-attendees';
import { ContactsPopover } from '../../../../mask-overlays/mask-overlay/views/ticket/components/contacts-popover/ContactsPopover';

interface UseTicketCustomActionsProps {
  tickets: Ticket[];
}
const eMailTemplateKind = EMailTemplateKind.TICKET;
const USER_REQUEST_DATA = {
  id: null,
  displayName: null,
  departmentName: null,
  eMail: null,
  hasLeft: null,
  firstName: null,
  lastName: null,
  loginName: null,
  userKind: null,
};

export const useTicketCustomActions = (props: UseTicketCustomActionsProps) => {
  const ticket = props.tickets?.[0];
  const dialogs = useDialogs();
  const { t } = useTranslation();

  const user = useUser();
  const usersRequest = useMemo<DataRequest>(() => {
    return {
      entity: Entities.user,
      data: USER_REQUEST_DATA,
      filter: [
        {
          id: {
            $eq: user.benutzerCode,
          },
        },
      ],
    };
  }, [user]);

  const userResponse = useDataProvider<User>(usersRequest);

  const { locked } = useLock();
  const templates = useEmailTemplates(eMailTemplateKind);
  const emailTemplatesRef = useRef<HTMLButtonElement>(null);

  const contactsPopoverData = useTicketContactsPopoverData(ticket?.id);
  const defaultTicketSenderAddress = useSetting(
    settings.defaultTicketSenderAddress()
  );

  const { value: standardTemplates } = useSetting(
    settings.standardEmailTemplates()
  );

  const [isEmailTemplatesPopoverOpen, setIsEmailTemplatesPopoverOpen] =
    useState(false);

  const openEmailMask = useCallback(
    (emaiTemplate: EMailTemplate | null) => {
      const template = !emaiTemplate?.id ? null : emaiTemplate;
      const businessPartner = ticket.businessPartner?.data;
      const businessPartnerType = sdObjTypeToEntity(
        ticket.businessPartner?.businessPartnerType
      );
      const contact = ticket.contact;

      const entityTemplate = businessPartner
        ? {
            entity: contact ? Entities.contact : Entities.customer,
            ids: [
              contact
                ? `${contact.id}:${businessPartnerType}:${businessPartner.id}`
                : businessPartner.id,
            ],
          }
        : undefined;

      const dialog = dialogs.open(MaskDialogOverlay, {
        entity: Entities.eMail,
        id: null,
        template: entityTemplate,
        onAfterSave: closeMaskOverlay,
        params: {
          basedon: 'EmailTemplate',
          emailTemplate: template ? JSON.stringify(template) : undefined,
          ticketTemplateContext: JSON.stringify(ticket),
          senderAddress: defaultTicketSenderAddress.value,
          forceNoTemplate: (template === null).toString(),
          processedMailTemplateArgs: JSON.stringify({
            noDirectContact: !contact,
          }),
        },
        close: closeMaskOverlay,
      });

      function closeMaskOverlay() {
        dialogs.close(dialog.id);
      }
    },
    [ticket, defaultTicketSenderAddress.value, dialogs]
  );

  const renderContactPopover = useCallback(
    ({ open, onClose, anchorEl }) => {
      return (
        <ContactsPopover
          popoverConfig={{
            open,
            anchorEl: anchorEl,
            onClose,
          }}
          {...contactsPopoverData}
        />
      );
    },
    [contactsPopoverData]
  );
  const emailPopover = useMemo(
    () => (
      <EmailTemplatesPopover
        open={isEmailTemplatesPopoverOpen}
        eMailTemplateKind={eMailTemplateKind}
        anchorEl={emailTemplatesRef.current}
        onClose={() => setIsEmailTemplatesPopoverOpen(false)}
        onTemplateClick={(template) => {
          setIsEmailTemplatesPopoverOpen(false);
          openEmailMask(template);
        }}
      />
    ),
    [isEmailTemplatesPopoverOpen, openEmailMask]
  );

  const actions = useMemo<CustomToolbarAction[]>(() => {
    const result: CustomToolbarAction[] = [];
    result.push({
      actionKey: 'createAppointmentFromTicket',
      title: t('APPOINTMENT.CREATE'),
      IconComponent: EditCalendarIcon,
      handler: (_, evt) => {
        const usersAttendees = mapUsersToAppointmentAttendees(
          userResponse.data
        );

        const appointmentAttendeeList = [...usersAttendees];

        if (ticket.contact)
          appointmentAttendeeList.push({
            contact: ticket.contact,
            contactId: ticket.contactId,
            displayName: ticket.contact.displayName,
          });

        // Get the current time
        const now = DateTime.now();

        // Calculate the next full 15-minute step
        const minutes = now.minute;
        const nextQuarter = Math.ceil(minutes / 15) * 15;
        let begin = now
          .set({ minute: 0, second: 0, millisecond: 0 })
          .plus({ minutes: nextQuarter });

        // If the calculated begin time goes into the next hour or day, adjust accordingly
        if (begin <= now) {
          begin = begin.plus({ minutes: 15 });
        }

        // Set end time to one hour after begin time
        const end = begin.plus({ hours: 1 });

        const dialog = dialogs.open(AppointmentQuickCreateDialog, {
          onClose: () => {
            dialogs.close(dialog.id);
          },
          template: {
            startDate: begin.toISO(),
            endDate: end.toISO(),
            appointmentAttendeeList,
            businessPartner: ticket.businessPartner,
            businessPartnerId: ticket.businessPartnerId,
            businessPartnerType: ticket.businessPartnerType,
            contactId: ticket.contactId,
            projectProcess: ticket.projectProcess,
            projectProcessId: ticket.projectProcessId,
            project: ticket.project,
            projectId: ticket.projectId,
            title: `${ticket.number} | ${ticket.title}`,
            note: htmlToPlainText(
              ticket.problemDescription?.replace(/<\/div>/g, '\n')
            ),
            contractId: ticket.contract?.id,
          },
        });
      },
      mode: 'Edit',
      sections: ['mobile', 'preview'],
      disabled: locked || !canAddAppointment(user),
      disableReason: canAddAppointment(user) ? undefined : t('RIGHTS.MISSING'),
    });

    result.push({
      actionKey: 'onEmailClicked',
      IconComponent: SendIcon,
      title: t('TICKET.SEND_EMAIL'),
      handler: (_, evt) => {
        emailTemplatesRef.current = evt.currentTarget as HTMLButtonElement;
        const standardTemplateId = standardTemplates[eMailTemplateKind];
        const standardTemplate = templates?.find(
          (t) => t.id === standardTemplateId
        );
        if (standardTemplate) {
          openEmailMask(standardTemplate);
        } else {
          setIsEmailTemplatesPopoverOpen(true);
        }
      },
      mode: 'Edit',
      sections: ['mobile', 'preview'],
      disabled: locked,
    });
    result.push({
      actionKey: 'onPhoneClick',
      IconComponent: PhoneIcon,
      title: t('FILE_CONTACTS.CALL'),
      handler: (_, evt) => {
        const dialog = dialogs.open(renderContactPopover, {
          onClose: () => dialogs.close(dialog.id),
          anchorEl: evt.currentTarget,
        });
      },
      mode: 'Edit',
      sections: ['mobile', 'preview'],
      disabled: locked,
    });
    return result;
  }, [
    t,
    user,
    userResponse.data,
    dialogs,
    ticket,
    openEmailMask,
    renderContactPopover,
    standardTemplates,
    templates,
    locked,
  ]);

  return useMemo(() => {
    return {
      actions,
      emailPopover,
    };
  }, [actions, emailPopover]);
};
